/**
 * EditorialReferenceRegister — a numbered reference register, optionally
 * grouped (Provider Data / Knowledge Base), each row a `[Ref N]` id, a
 * source (with a bold category + optional link), and a right-aligned
 * retrieved date.
 *
 * Editorial reference: `.refgroup` / `.ref` in
 *   docs/design/round3-conclusion-extended-refs.html (Refs 22A grouped, 22B flat):
 *     group header: mono small-caps accent, optional muted count;
 *     row: [Ref N] (mono accent) · source (var(--body), <b> var(--ink),
 *       <a> var(--accent)) · retrieved-date (mono muted), border-bottom
 *       1px solid var(--hair).
 *
 * Used by:
 *   - References & Appendix (22A grouped / 22B flat register)
 *
 * Rows whose `source` is empty are dropped; an empty register returns null.
 * Links open in a new tab with rel="noreferrer" (never inject the Maps key
 * etc. — callers pass display hrefs only).
 *
 * Theme tokens read: 'accent' (id + group head + link), 'body' (source),
 * 'ink' (bold category), 'muted' (date + count + flat-row category tag),
 * 'hair' (row rule). Mono id/date via --report-body-font. Pure / print-safe.
 */
import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

function ExternalLinkMark(): React.ReactElement {
  return (
    <ArrowUpRight
      aria-hidden="true"
      data-pdf-external-link-mark="true"
      style={{
        display: 'inline-block',
        width: '0.8em',
        height: '0.8em',
        verticalAlign: 'baseline',
      }}
      strokeWidth={1.75}
    />
  );
}

export interface ReferenceRow {
  /** Numeric id rendered as `[Ref N]` (grouped) or `[N]` (flat). */
  id: number;
  /** Bold category lead, e.g. "Climate", "Grid". */
  category?: React.ReactNode;
  /** Source description, e.g. "Open-Meteo ERA5 Reanalysis". */
  source: React.ReactNode;
  /** Optional display host/link text, e.g. "open-meteo.com". */
  linkText?: React.ReactNode;
  /** Optional href (display only — never inject secrets). */
  href?: string;
  /** Retrieved / accessed date, e.g. "2026-05-17". */
  date?: React.ReactNode;
  /** Provenance tag for the flat variant, e.g. "PROVIDER" / "KB". */
  tag?: React.ReactNode;
}

export interface ReferenceGroup {
  heading: React.ReactNode;
  /** Optional muted count/desc on the group head. */
  meta?: React.ReactNode;
  rows: ReferenceRow[];
}

export interface EditorialReferenceRegisterProps {
  theme: ReportTheme;
  /** Provide EITHER grouped (22A) OR flat (22B) rows. */
  groups?: ReferenceGroup[];
  rows?: ReferenceRow[];
  /** Id prefix; defaults 'Ref ' for grouped, '' for flat. */
  idPrefix?: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

function isRenderableSource(node: React.ReactNode): boolean {
  if (node == null || node === false) return false;
  if (typeof node === 'string') return node.trim().length > 0;
  return true;
}

export function EditorialReferenceRegister({
  theme,
  groups,
  rows,
  idPrefix,
  id,
  className,
  style,
}: EditorialReferenceRegisterProps): React.ReactElement | null {
  const accent = roleColor(theme, 'accent');
  const bodyInk = roleColor(theme, 'body');
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const hair = roleColor(theme, 'hair');

  const prefix = idPrefix ?? (groups ? 'Ref ' : '');

  const renderRow = (row: ReferenceRow, key: number): React.ReactElement => (
    <div
      key={key}
      style={{
        display: 'grid',
        gridTemplateColumns: '46px 1fr auto',
        gap: 12,
        alignItems: 'baseline',
        padding: '6px 0',
        borderBottom: `1px solid ${hair}`,
        // 13px reference-row size.
        fontSize: 13,
      }}
    >
      <span style={{ fontFamily: MONO, fontSize: REPORT_TEXT_PX.caption, color: accent }}>
        {prefix ? `[${prefix}${row.id}]` : `[${row.id}]`}
      </span>
      <span style={{ color: bodyInk }}>
        {row.category != null && <b style={{ color: ink }}>{row.category}</b>}
        {row.category != null && ' – '}
        {row.source}
        {row.linkText != null && (
          <>
            {' · '}
            {row.href ? (
              <a
                href={row.href}
                target="_blank"
                rel="noreferrer"
                style={{ color: accent, textDecoration: 'none' }}
              >
                {row.linkText} <ExternalLinkMark />
              </a>
            ) : (
              <span style={{ color: accent }}>{row.linkText} <ExternalLinkMark /></span>
            )}
          </>
        )}
        {row.tag != null && (
          <span
            style={{ fontFamily: MONO, fontSize: REPORT_TEXT_PX.micro, color: muted, marginLeft: 8 }}
          >
            {row.tag}
          </span>
        )}
      </span>
      <span style={{ fontFamily: MONO, fontSize: REPORT_TEXT_PX.caption, color: muted, whiteSpace: 'nowrap' }}>
        {row.date}
      </span>
    </div>
  );

  if (groups) {
    const renderGroups = groups
      .map((g) => ({ ...g, rows: g.rows.filter((r) => isRenderableSource(r.source)) }))
      .filter((g) => g.rows.length > 0);
    if (renderGroups.length === 0) return null;
    let key = 0;
    return (
      <div id={id} className={className} style={style}>
        {renderGroups.map((group, g) => (
          <div key={g} style={{ marginTop: g === 0 ? 0 : 14 }}>
            <div
              style={{
                fontFamily: MONO,
                fontSize: REPORT_TEXT_PX.caption,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: accent,
                marginBottom: 8,
              }}
            >
              {group.heading}
              {group.meta != null && (
                <span style={{ color: muted, textTransform: 'none', letterSpacing: '0.02em' }}>
                  {' '}
                  {group.meta}
                </span>
              )}
            </div>
            {group.rows.map((row) => renderRow(row, (key += 1)))}
          </div>
        ))}
      </div>
    );
  }

  const flat = (rows ?? []).filter((r) => isRenderableSource(r.source));
  if (flat.length === 0) return null;
  return (
    <div id={id} className={className} style={style}>
      {flat.map((row, i) => renderRow(row, i))}
    </div>
  );
}
